// Updated: 2026-09-07
//
// `PublicApiError` (the status-bearing public-fetch error) used to be declared privately in this
// module. Slice 2 needs the same shape in `patterns.ts`, so it moved to `./errors` — same
// `{ name: 'PublicApiError', status }` contract, one definition, still not re-exported from the
// fetchers barrel (react-query-pattern-detail, task 1).
import type {
  PublicMaterialApiResponse,
  MaterialPageResponse,
  SearchPublicMaterialsResponse,
  SearchParentMaterialsResponse,
  ParentMaterialSearchResult,
  LinkMaterialToPublicResponse,
  CreateLinkSuggestionResponse,
  MaterialSearchDocument,
  PaginatedMaterialReviewsResponse,
  CopyMaterialResponse,
  DiscoverMaterialsResponse,
  ViewMaterialResponse,
} from '@mrb91002/shared-types'

import { API_BASE, SECURE_API_BASE } from '../config'
import { secureFetch } from '../secure/helpers'

import { PublicApiError } from './errors'

export async function searchPublicMaterials(query: string): Promise<MaterialSearchDocument[]> {
  const response = await fetch(`${API_BASE}/materials/search?q=${encodeURIComponent(query)}`)
  if (!response.ok) {
    throw new Error('Failed to search materials')
  }
  const data: SearchPublicMaterialsResponse = await response.json() as SearchPublicMaterialsResponse
  return data.items || []
}

export async function searchParentMaterials(query: string, idToken: string, limit = 20): Promise<ParentMaterialSearchResult[]> {
  const response = await fetch(
    `${SECURE_API_BASE}/materials/search-parents?q=${encodeURIComponent(query)}&limit=${limit}`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  )
  if (!response.ok) throw new Error('Failed to search parent materials')
  const data: SearchParentMaterialsResponse = await response.json() as SearchParentMaterialsResponse
  return data.materials || []
}

export async function linkMaterial(materialId: string, publicMaterialId: string, idToken: string): Promise<LinkMaterialToPublicResponse> {
  return secureFetch<LinkMaterialToPublicResponse>(`${SECURE_API_BASE}/craft-room/material/${materialId}/link`, idToken, {
    method: 'POST',
    body: JSON.stringify({ public_material_id: publicMaterialId }),
  })
}

export async function suggestMaterialLink(
  privateMaterialId: string,
  publicMaterialId: string,
  idToken: string,
  ownerScreenName?: string,
): Promise<CreateLinkSuggestionResponse> {
  return secureFetch<CreateLinkSuggestionResponse>(`${SECURE_API_BASE}/material-link-suggestions`, idToken, {
    method: 'POST',
    // `owner_screen_name` identifies WHOSE material this is — the suggester (caller) is usually NOT the
    // owner, so the backend resolves the owner's craft room from this name. Omitted only on the legacy
    // self path.
    body: JSON.stringify({
      private_material_id: privateMaterialId,
      public_material_id: publicMaterialId,
      ...(ownerScreenName ? { owner_screen_name: ownerScreenName } : {}),
    }),
  })
}

export async function fetchMaterialReviews(
  materialId: string,
  limit = 10,
  lastKey: string | null = null,
): Promise<PaginatedMaterialReviewsResponse> {
  let url = `${API_BASE}/materials/${materialId}/reviews?limit=${limit}`
  if (lastKey) url += `&lastKey=${encodeURIComponent(lastKey)}`
  const resp = await fetch(url)
  if (!resp.ok) throw new Error('Failed to fetch material reviews')
  return resp.json() as Promise<PaginatedMaterialReviewsResponse>
}

export async function fetchMaterialDetails(
  materialId: string,
  includeUserReview: boolean = false,
  idToken?: string,
): Promise<PublicMaterialApiResponse> {
  const url = `${API_BASE}/materials/${materialId}${includeUserReview ? '?includeUserReview=true' : ''}`
  const resp = await fetch(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  if (!resp.ok) throw new Error('Failed to fetch material details')
  return resp.json() as Promise<PublicMaterialApiResponse>
}

/**
 * Public material page payload (lambda-main `GET /api/materials/{id}`).
 *
 * `craftRoomName` is part of RESOURCE IDENTITY, not a filter: supplying it returns the personal
 * `mat-*` material inside that user's craft room instead of the public view. `opts.signal` is
 * optional request cancellation forwarded from a React Query `queryFn`; both extra parameters are
 * additive, so existing callers are unaffected.
 *
 * On failure this throws a status-bearing error so the query layer can branch on the HTTP status
 * (notably: never retry 4xx) — success parsing and the request contract are unchanged.
 */
export async function fetchMaterialPage(
  materialId: string,
  idToken?: string,
  craftRoomName?: string,
  opts?: { signal?: AbortSignal },
): Promise<MaterialPageResponse> {
  let url = `${API_BASE}/materials/${materialId}`
  // craftRoomName is REQUIRED for personal materials (mat-*)
  // It's used to look up the identity_id from screen name
  if (craftRoomName) {
    url += `?craftRoomName=${encodeURIComponent(craftRoomName)}`
  }
  const resp = await fetch(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to fetch material page data', resp.status)
  return resp.json() as Promise<MaterialPageResponse>
}

export async function copyMaterial(
  sourceMaterialId: string,
  sourceScreenName: string,
  customizations: {
    quantity?: number
    storage_location?: string
    notes?: string
  },
  idToken: string,
): Promise<CopyMaterialResponse> {
  const url = `${SECURE_API_BASE}/craft-room/materials/copy`
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      source_material_id: sourceMaterialId,
      source_screen_name: sourceScreenName,
      customizations,
    }),
  })
  if (!resp.ok) {
    const error = await resp.json()
    throw new Error(error.error || 'Failed to copy material')
  }
  return resp.json() as Promise<CopyMaterialResponse>
}

export async function addPublicMaterial(
  publicMaterialId: string,
  customizations: {
    quantity?: number
    storage_location?: string
    notes?: string
  },
  idToken: string,
): Promise<CopyMaterialResponse> {
  const url = `${SECURE_API_BASE}/craft-room/materials/add-public`
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      public_material_id: publicMaterialId,
      customizations,
    }),
  })
  if (!resp.ok) {
    const error = await resp.json()
    throw new Error(error.error || 'Failed to add public material')
  }
  return resp.json() as Promise<CopyMaterialResponse>
}

/**
 * Browse the PUBLIC MATERIAL CATALOG, newest first
 * (lambda-secure `GET /api/secure/craft-room/materials/discover`).
 *
 * As of 2026-09-07 this reads the catalog (`pub-mat-` entries) out of OpenSearch, NOT other users'
 * personal craft-room materials, so every caller gets the SAME list — the read is identity-INVARIANT
 * and the items carry `contributed_by` rather than an owner `screen_name`. It is still token-gated
 * because `material-copying.ts#discoverMaterials` resolves the caller with `getIdentityId(event)` and
 * answers 401 without one; the query layer keeps the `'me'` key and `enabled: !!idToken` for that
 * reason, not because the response varies (react-query-browse-search, Req 4.2).
 *
 * The read is EVENTUALLY CONSISTENT (OpenSearch), unlike the GSI query it replaced.
 *
 * `opts.signal` and the status-bearing `PublicApiError` are additive: the URL, the `limit`/`category`
 * parameters, the optional Bearer header, and the success parsing are unchanged, so a caller that omits
 * `opts` makes a byte-identical request (Req 6.1, 6.2, 6.3).
 */
export async function discoverMaterials(
  limit: number = 20,
  category?: string,
  idToken?: string,
  opts?: { signal?: AbortSignal },
): Promise<DiscoverMaterialsResponse> {
  let url = `${SECURE_API_BASE}/craft-room/materials/discover?limit=${limit}`
  if (category) url += `&category=${encodeURIComponent(category)}`

  const resp = await fetch(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  if (!resp.ok) throw new PublicApiError('Failed to discover materials', resp.status)
  return resp.json() as Promise<DiscoverMaterialsResponse>
}

// `getPersonalMaterialUsage` (GET /api/secure/materials/personal/{id}/usage) was removed here by
// `material-usage-authority` task 11.2 (Req 10.1). Its only caller was the usage stat block in
// `components/pages/CraftRoom/MaterialDetailsModal.tsx`, removed in the same change.
//
// The ENDPOINT it called is still registered in `lambda-secure`, deliberately and against this
// task's plan — removing it moves two checked-in audit artifacts whose drift tests carry hardcoded
// route counts, and it is still driven directly by `integration-tests/flows/20-materials` Part B.
// See BL-169. So after this change the route has no website consumer at all.

export async function viewMaterialWithDuplicateCheck(
  materialId: string,
  screenName: string,
  idToken: string,
): Promise<ViewMaterialResponse> {
  const url = `${SECURE_API_BASE}/craft-room/materials/view/${materialId}?screenName=${encodeURIComponent(screenName)}`
  const resp = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })
  if (!resp.ok) throw new Error('Failed to fetch material with duplicate check')
  return resp.json() as Promise<ViewMaterialResponse>
}
